import { Box, Button, Heading, Text, useToast } from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout, Logo } from 'components/atoms'
import { InputBuild } from 'components/molecules'
import { regexForm } from 'components/utils/regexForm'
import { useAuthContext } from 'contexts/auth'
import { environments } from 'environments/environments'
import React, { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useForm } from 'react-hook-form'
import { Link as LinkRouter, useNavigate, useParams } from 'react-router-dom'

import { getStatus, patchStatus } from 'services/users'
import * as yup from 'yup'
import { CustomError } from '../SignIn/interface'
import PasswordListError from '../components/PasswordListError'
import { errorsAuth } from '../utils/errorsAuth'
import { InputFormRegistration } from './form'
import { FormRegistrationElementsValues } from './interface'
import { InputFormRegistrationElements } from './types'

const Registration: React.FC = () => {
    const toast = useToast()
    const navigate = useNavigate()
    const { id = '' } = useParams()
    const [searching, setSearching] = useState(true)
    const { validateCaptcha } = useAuthContext()
    const [captcha, setCaptcha] = useState('')
    const { CAPTCHA_SITE_KEY } = environments

    const recaptchaRef = useRef<ReCAPTCHA>(null)

    const schema = yup
        .object({
            nome: yup.string().required().trim(),
            nomeUsuario: yup.string().required().trim(),
            cpf: yup.string().matches(regexForm.cpf).required(),
            senha: yup
                .string()
                .min(8, 'Senha deve ter pelo menos 8 caracteres')
                .max(30, 'Senha deve ter no máximo 30 caracteres')
                .matches(regexForm.senha)
                .required(),
            senhaConfirmacao: yup
                .string()
                .oneOf([yup.ref('senha'), null], 'Senhas não coincidem.')
                .required()
        })
        .required()

    const {
        handleSubmit,
        register,
        watch,
        formState: { errors, isSubmitting },
        setValue
    } = useForm<FormRegistrationElementsValues>({
        resolver: yupResolver(schema)
    })

    const userRegistration = ({
        nomeUsuario,
        senha
    }: {
        nomeUsuario: string
        senha: string
    }) => {
        patchStatus({ id, nomeUsuario, senha })
            .then(() => {
                navigate('/entrar/')
            })
            .catch(() => {
                toast({
                    title: 'Dados incorretos.',
                    description: 'Verifique os dados e tente novamente.',
                    position: 'bottom-left',
                    status: 'error',
                    isClosable: true
                })
            })
    }

    const validateRegistrationCaptcha = ({
        nomeUsuario,
        senha
    }: {
        nomeUsuario: string
        senha: string
    }) => {
        if (process.env.REACT_APP_ENV === 'qa') {
            return userRegistration({ nomeUsuario, senha })
        }

        if (captcha) {
            validateCaptcha(captcha)
                .then(() => {
                    return userRegistration({ nomeUsuario, senha })
                })
                .catch((error: CustomError) => {
                    recaptchaRef.current?.reset()

                    error.response.data
                        .filter(
                            (elem, index, arr) =>
                                index ===
                                arr.findIndex(
                                    checkEle => checkEle.codigo === elem.codigo
                                )
                        )
                        .map(res => {
                            const error = errorsAuth[res.codigo]
                            toast({
                                title: error.title,
                                description: error.description,
                                position: 'bottom-left',
                                status: 'error',
                                isClosable: true,
                                variant: 'solid-error'
                            })
                        })
                })
        } else {
            toast({
                title: 'Erro!',
                description: 'Por favor, confirme que você não é um robô.',
                position: 'bottom-left',
                status: 'error',
                isClosable: true,
                variant: 'solid-error'
            })
        }
    }

    const onSubmit = async ({
        nomeUsuario,
        senha
    }: FormRegistrationElementsValues) => {
        return validateRegistrationCaptcha({ nomeUsuario, senha })
    }

    useEffect(() => {
        id &&
            getStatus(id)
                .then(({ data, status }) => {
                    switch (status) {
                        case 204:
                            navigate(`/confirmacao/${id}`)
                            break
                        case 200:
                            if (data) {
                                setValue('nome', data.nome)
                                setValue('cpf', data.cpf)
                            }
                            break
                        default:
                            break
                    }
                })
                .catch(() => navigate(`/entrar`))
                .finally(() => setSearching(false))
    }, [])

    return (
        <Box
            display="flex"
            flexDirection="column"
            overflow="scroll"
            justifyContent="center"
            alignItems="center"
            color="blackAlpha.900"
            bgColor="backdrop"
            w="100%"
            h="100vh"
            pt={10}
        >
            <Box display="grid" w="100%" maxW="2xl">
                <Box display="flex" alignItems="center" py={23}>
                    <Logo maxW={187} />
                </Box>
                <Box
                    bg="white"
                    borderRadius="lg"
                    px={{ base: 4, md: 6 }}
                    py={{ base: 4, md: 12 }}
                >
                    <Heading size="md" textAlign="center" mb="2">
                        Bem vind@ ao sistema!
                    </Heading>
                    <Text textAlign="center">
                        Preencha os campos a seguir para finalizar o seu
                        cadastro
                    </Text>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Layout type="container" full px={6} mt={8}>
                            <Layout type="row" gutter={{ y: 1.5 }}>
                                {InputFormRegistration.map(
                                    ({ layout, input }) => (
                                        <Layout {...layout}>
                                            <InputBuild
                                                {...input}
                                                isSearching={searching}
                                                err={
                                                    errors[
                                                        `${
                                                            input.id as InputFormRegistrationElements
                                                        }`
                                                    ]
                                                }
                                                {...register(
                                                    `${
                                                        input.id as InputFormRegistrationElements
                                                    }`
                                                )}
                                                value={watch(
                                                    `${
                                                        input.id as InputFormRegistrationElements
                                                    }`
                                                )}
                                            />
                                        </Layout>
                                    )
                                )}
                                <Layout type="col" layout={{ base: 12 }}>
                                    <PasswordListError
                                        password={watch('senha')}
                                    />
                                </Layout>

                                <Layout
                                    type="col"
                                    display="flex"
                                    justifyContent="center"
                                    layout={{ base: 12 }}
                                >
                                    <ReCAPTCHA
                                        ref={recaptchaRef}
                                        sitekey={CAPTCHA_SITE_KEY}
                                        onChange={e => setCaptcha(e as string)}
                                    />
                                </Layout>

                                <Layout type="col" layout={{ base: 12, md: 6 }}>
                                    <Button
                                        as={LinkRouter}
                                        size="md"
                                        w="full"
                                        colorScheme="primary"
                                        variant="outline"
                                        to="/"
                                        data-testid="btn-cancel"
                                    >
                                        Cancelar
                                    </Button>
                                </Layout>
                                <Layout type="col" layout={{ base: 12, md: 6 }}>
                                    <Button
                                        size="md"
                                        w="full"
                                        colorScheme="primary"
                                        type="submit"
                                        isLoading={isSubmitting}
                                        data-testid="btn-submit"
                                    >
                                        Finalizar
                                    </Button>
                                </Layout>
                            </Layout>
                        </Layout>
                    </form>
                </Box>
            </Box>
        </Box>
    )
}

export default Registration
